import { useEffect, useState } from "react";
import DonutChart from "../../components/DonutChart";
import axiosInstance from "../../api/axiosInstance";
import { Shades } from "../../utils/common";

const Stats = () => {
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [dmStatsLoading, setDmStatsLoading] = useState(false);
  const [dmStats, setDmStats] = useState(null);
  const [timePeriod, setTimePeriod] = useState("today");

  const colors = [
    "#06d6a0", // teal (Found)
    "#ffd166", // yellow (Not Found L1)
    "#ef476f", // red (Not Found L2)
    "#118ab2", // blue (Deleted)
    "#8338ec", // purple (DM Leads)
    "#ff6b6b", // coral (Saved)
    "#4cc9f0", // sky blue (Not Saved)
    "#f72585", // pink
    "#4361ee", // indigo
    "#2a9d8f", // green
  ];

  const fetchStats = async ({ showLoader = true }) => {
    try {
      if (showLoader) setStatsLoading(true);
      const resp = await axiosInstance.get(`api/ispeed_properties/stats?tables=leads_m,leads_b,leads_gs&timePeriod=${timePeriod}`);
      if (resp.status === 200) {
        setStats(resp.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStatsDMLeads = async ({ showLoader = true }) => {
    try {
      if (showLoader) setDmStatsLoading(true);
      const resp = await axiosInstance.get(`api/ispeed_properties/stats-dm-leads?sources=m,b,g,none&timePeriod=${timePeriod}`);

      if (resp.status === 200) {
        setDmStats(resp.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setDmStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats({ showLoader: true });
    fetchStatsDMLeads({ showLoader: true });

    const interval = setInterval(() => {
      fetchStats({ showLoader: false });
      fetchStatsDMLeads({ showLoader: false });
    }, 10000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, [timePeriod]);

  const chartConfigs = stats
    ? [
        {
          key: "leads_m",
          title: "Leads M",
          data: [stats.leads_m.created_leads, stats.leads_m.not_found_l1_created, stats.leads_m.not_found_l2_created, stats.leads_m.found_created, stats.leads_m.deleted_created, stats.leads_m.dm_created, stats.leads_m.contact_saved_updated, stats.leads_m.others_created],
          labels: ["Added", "Not Found L-1", "Not Found L-2", "Found", "Deleted", "DM Leads", "GHL Saved", "GHL Not Saved"],
          colors,
        },
        {
          key: "leads_b",
          title: "Leads B",
          data: [stats.leads_b.created_leads, stats.leads_b.not_found_l1_created, stats.leads_b.not_found_l2_created, stats.leads_b.found_created, stats.leads_b.deleted_created, stats.leads_b.dm_created, stats.leads_b.contact_saved_updated, stats.leads_b.others_created],
          labels: ["Added", "Not Found L-1", "Not Found L-2", "Found", "Deleted", "DM Leads", "GHL Saved", "GHL Not Saved"],
          colors,
        },
        // {
        //   key: "leads_s",
        //   title: "Leads S",
        //   data: [stats.leads_s.created_leads, stats.leads_s.not_found_l1_created, stats.leads_s.not_found_l2_created, stats.leads_s.found_created, stats.leads_s.deleted_created, stats.leads_s.dm_created, stats.leads_s.contact_saved_updated, stats.leads_s.others_created],
        //   labels: ["Added", "Not Found L-1", "Not Found L-2", "Found", "Deleted", "DM Leads", "GHL Saved", "GHL Not Saved"],
        //   colors,
        // },
        {
          key: "leads_gs",
          title: "Leads G",
          data: [stats.leads_gs.created_leads, stats.leads_gs.not_found_l1_created, stats.leads_gs.not_found_l2_created, stats.leads_gs.found_created, stats.leads_gs.deleted_created, stats.leads_gs.dm_created, stats.leads_gs.contact_saved_updated, stats.leads_gs.others_created],
          labels: ["Added", "Not Found L-1", "Not Found L-2", "Found", "Deleted", "DM Leads", "GHL Saved", "GHL Not Saved"],
          colors,
        },
      ]
    : [];

  const chartConfigsTotal = stats
    ? [
        {
          key: "leads_total",
          title: "Total Stats",
          data: [stats.leads_m.created_leads + stats.leads_b.created_leads + stats.leads_gs.created_leads, stats.leads_m.not_found_l1_created + stats.leads_b.not_found_l1_created + stats.leads_gs.not_found_l1_created, stats.leads_m.not_found_l2_created + stats.leads_b.not_found_l2_created + stats.leads_gs.not_found_l2_created, stats.leads_m.found_created + stats.leads_b.found_created + stats.leads_gs.found_created, stats.leads_m.deleted_created + stats.leads_b.deleted_created + stats.leads_gs.deleted_created, stats.leads_m.dm_created + stats.leads_b.dm_created + stats.leads_gs.dm_created, stats.leads_m.contact_saved_updated + stats.leads_b.contact_saved_updated + stats.leads_gs.contact_saved_updated, stats.leads_m.others_created + stats.leads_b.others_created + stats.leads_gs.others_created],
          labels: ["Added", "Not Found L-1", "Not Found L-2", "Found", "Deleted", "DM Leads", "GHL Saved", "GHL Not Saved"],
          colors,
        },
      ]
    : [];

  const chartConfigsDM = dmStats
    ? [
        {
          key: "dm_leads_all",
          title: "DM Leads",
          data: [dmStats.m?.contact_saved_updated + dmStats.b?.contact_saved_updated + dmStats.g?.contact_saved_updated + dmStats.none?.contact_saved_updated, dmStats.m?.others_created + dmStats.b?.others_created + dmStats.g?.others_created + dmStats.none?.others_created, dmStats.m?.contact_saved_updated, dmStats.m?.others_created, dmStats.b?.contact_saved_updated, dmStats.b?.others_created, dmStats.g?.contact_saved_updated, dmStats.g?.others_created, dmStats.none?.contact_saved_updated],
          labels: ["GHL Saved", "GHL Not Saved", "M GHL Saved", "M GHL Not Saved", "B GHL Saved", "B GHL Not Saved", "G GHL Saved", "G GHL Not Saved", "Unknow GHL Saved"],
          colors: ["#ff6b6b", "#4cc9f0"],
        },
      ]
    : [];

  const Periods = {
    today: "Today",
    yesterday: "Yesterday",
    last7: "Last 7 Days",
    last15: "Last 15 Days",
    last30: "last 30 Days",
  };

  return (
    <>
      <div className="!mx-auto rounded-xl shadow">{/* <DataChart /> */}</div>
      <div className="input_fields_container !grid md:!grid-cols-3 !gap-4 ">
        <div className="w-full">
          <select
            className="w-full !p-3 rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm 
                 focus:outline-none focus:ring-2 focus:ring-[#ff850f] focus:border-[#ff850f] 
                 transition cursor-pointer"
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
          >
            {Object.entries(Periods).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-6 !mt-10 !mb-8 !px-4 md:!grid-cols-2">
        {chartConfigsDM.map((cfg) => (
          <div key={cfg.key} className="h-96 bg-white shadow-md shadow-slate-300 rounded flex items-center justify-center border border-gray-200">
            <DonutChart data={cfg.data} labels={cfg.labels} title={cfg.title} legendPosition="bottom" cutout="60%" backgroundColors={cfg.colors} loading={dmStatsLoading} rounded donutWidth={20} colors={Shades.sky} width="300px" height="300px" />
          </div>
        ))}
        {chartConfigsTotal.map((cfg) => (
          <div key={cfg.key} className="h-96 bg-white shadow-md shadow-slate-300 rounded flex items-center justify-center border border-gray-200">
            <DonutChart data={cfg.data} labels={cfg.labels} title={cfg.title} legendPosition="bottom" cutout="60%" backgroundColors={cfg.colors} loading={statsLoading} rounded donutWidth={20} colors={Shades.emerald} width="300px" height="300px" />
          </div>
        ))}
      </div>

      <div className="grid gap-x-6 gap-y-8 !mt-10 !mb-8 !px-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))" }}>
        {chartConfigs.map((cfg) => (
          <div key={cfg.key} className="h-72 bg-white shadow-md shadow-slate-300 rounded flex items-center justify-center border border-gray-200">
            <DonutChart data={cfg.data} labels={cfg.labels} title={cfg.title} legendPosition="bottom" cutout="60%" backgroundColors={cfg.colors} loading={statsLoading} rounded donutWidth={20} width="200px" height="200px" />
          </div>
        ))}
      </div>
    </>
  );
};

export default Stats;
